import {DuotoneIcon, DuotoneIconName, Icon, isDuotoneIconName} from '@benchling/icons';
import React from 'react';

import * as logging from 'util/Logging';

import {BENCHLING_ICON_ALIASES, isIconAlias, resolveIconAlias} from './IconAliases';

export type BenchlingDuotoneIconName =
  | DuotoneIconName
  // Include all aliases to `DuotoneIconName` icons
  | Extract<(typeof BENCHLING_ICON_ALIASES)[number], readonly [string, DuotoneIconName]>[0];

export function isBenchlingDuotoneIconName(iconName: string): iconName is BenchlingDuotoneIconName {
  return (
    isDuotoneIconName(iconName) || (isIconAlias(iconName) && isDuotoneIconName(resolveIconAlias(iconName)))
  );
}

export interface BenchlingDuotoneIconProps {
  icon: BenchlingDuotoneIconName;
  /** This should generally be the darker / defining color of the icon */
  primaryColor: string;
  /** This should generally be the lighter highlight color of the icon */
  secondaryColor: string;
  onClick?: (e: React.MouseEvent) => void;
  /** Whether to spin the icon (typically used with loading icons, e.g. "spinner-third") */
  spin?: boolean;
  altText?: string;
}

/**
 * Render a duotone icon.
 *
 * If you would like to add a new duotone icon, please use the "Request new icon" slash-command in Slack,
 * or reach out in #design-systems.
 */
export default function BenchlingDuotoneIcon({
  icon,
  primaryColor,
  secondaryColor,
  onClick,
  spin,
  altText,
}: BenchlingDuotoneIconProps): JSX.Element {
  if (isBenchlingDuotoneIconName(icon)) {
    return (
      <DuotoneIcon
        icon={isIconAlias(icon) ? (resolveIconAlias(icon) as DuotoneIconName) : icon}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        onClick={onClick}
        spin={spin}
        altText={altText}
      />
    );
  } else {
    logging.warn(`Icon '${icon}' missing. Using notdef by default.`);
    return <Icon icon="notdef" />;
  }
}
